import { i18n } from "@lingui/core";
import { languages } from "@stylingcv/utils";
import dayjs from "dayjs";

import { dayjsLocales } from "./dayjs";

export const defaultLocale = "en-US";

export async function dynamicActivate(locale: string) {
  try {
    // Normalize locale to match directory structure (e.g., en-US, ar-SA)
    const normalizedLocale =
      languages.find(
        (lang) =>
          lang.locale.toLowerCase() === locale.toLowerCase() ||
          lang.id.toLowerCase() === locale.toLowerCase(),
      )?.locale || locale;

    // Import the compiled translation module
    const module = await import(`../locales/${normalizedLocale}/messages.mjs`);

    // Handle different export formats from @lingui/vite-plugin
    let messages: any = {};

    // Priority order: direct .messages, then .default.messages, then .default, then module itself
    if (module.messages) {
      messages = module.messages;
    } else if (module.default?.messages) {
      messages = module.default.messages;
    } else if (module.default && typeof module.default === "object") {
      messages = module.default;
    } else {
      messages = module;
    }

    // Ensure we have a valid messages object
    if (!messages || typeof messages !== "object" || Object.keys(messages).length === 0) {
      throw new Error(`No valid translation messages found in module for locale: ${locale}`);
    }

    // PILLAR 3: English Fallback Strategy
    // Always load default locale (en-US) as the base layer
    if (normalizedLocale !== defaultLocale) {
      const defaultModule = await import(`../locales/${defaultLocale}/messages.mjs`);
      let defaultMessages: any = {};
      
      if (defaultModule.messages) {
        defaultMessages = defaultModule.messages;
      } else if (defaultModule.default?.messages) {
        defaultMessages = defaultModule.default.messages;
      } else if (defaultModule.default) {
        defaultMessages = defaultModule.default;
      } else {
        defaultMessages = defaultModule;
      }
      
      i18n.load(defaultLocale, defaultMessages);
    }

    // Load and activate the target locale
    i18n.load(normalizedLocale, messages);
    i18n.activate(normalizedLocale);

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (dayjsLocales[locale]) {
      dayjs.locale(await dayjsLocales[locale]());
    }
  } catch (error) {
    console.error(`[Lingui] Failed to load messages for locale: ${locale}`, error);
    // Fallback to default locale if current locale fails and it's not already default
    if (locale === defaultLocale) {
      // If even default locale fails, load with empty messages
      // This should not happen in production with proper build
      console.error("[Lingui] Failed to load default locale messages - using empty messages");
      i18n.loadAndActivate({ locale, messages: {} });
    } else {
      await dynamicActivate(defaultLocale);
    }
  }
}
